<template>
  <div class="shop-management">
    <div class="page-header">
      <h2>店铺管理</h2>
      <el-button type="primary" @click="showCreateDialog">新增店铺</el-button>
    </div>

    <!-- 搜索表单 -->
    <el-form :inline="true" :model="searchForm" class="search-form">
      <el-form-item label="店铺名称">
        <el-input v-model="searchForm.shop_name" placeholder="请输入店铺名称" clearable />
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="searchForm.status" placeholder="请选择状态" clearable>
          <el-option label="启用" :value="1" />
          <el-option label="禁用" :value="0" />
        </el-select>
      </el-form-item>
      <el-form-item label="店铺类型">
        <el-select v-model="searchForm.shop_type" placeholder="请选择店铺类型" clearable>
          <el-option label="个人" :value="1" />
          <el-option label="企业" :value="2" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleSearch">搜索</el-button>
        <el-button @click="handleReset">重置</el-button>
      </el-form-item>
    </el-form>

    <!-- 数据表格 -->
    <el-table v-loading="loading" :data="shopList" border style="width: 100%">
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="shop_name" label="店铺名称" />
      <el-table-column prop="shop_logo" label="店铺LOGO" width="100">
        <template slot-scope="scope">
          <el-image v-if="scope.row.shop_logo" :src="scope.row.shop_logo" style="width: 50px; height: 50px" fit="cover" />
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column prop="owner_name" label="店主姓名" />
      <el-table-column prop="owner_mobile" label="店主手机" />
      <el-table-column prop="contact_phone" label="联系电话" />
      <el-table-column prop="address" label="地址" />
      <el-table-column prop="business_hours" label="营业时间" />
      <el-table-column prop="shop_type" label="店铺类型" width="100">
        <template slot-scope="scope">
          <el-tag :type="scope.row.shop_type == 1 ? 'primary' : 'success'">
            {{ scope.row.shop_type == 1 ? '个人' : '企业' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="status" label="状态" width="100">
        <template slot-scope="scope">
          <el-tag :type="scope.row.status == 1 ? 'success' : 'danger'">
            {{ scope.row.status == 1 ? '启用' : '禁用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="create_time" label="创建时间" width="180" />
      <el-table-column label="操作" width="220" fixed="right">
        <template slot-scope="scope">
          <div class="table-operations">
            <el-button size="mini" type="text" @click="handleView(scope.row)">查看</el-button>
            <el-button size="mini" type="text" class="edit-btn" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button size="mini" type="text" class="delete-btn" @click="handleDelete(scope.row)">删除</el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination">
      <el-pagination
        :current-page="pagination.page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.limit"
        :total="pagination.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <!-- 创建/编辑对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="800px">
      <el-form ref="shopForm" :model="shopForm" :rules="shopRules" label-width="120px">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="店铺名称" prop="shop_name">
              <el-input v-model="shopForm.shop_name" placeholder="请输入店铺名称" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="店主姓名" prop="owner_name">
              <el-input v-model="shopForm.owner_name" placeholder="请输入店主姓名" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="店主手机" prop="owner_mobile">
              <el-input v-model="shopForm.owner_mobile" placeholder="请输入店主手机号" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="联系电话" prop="contact_phone">
              <el-input v-model="shopForm.contact_phone" placeholder="请输入联系电话" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-form-item label="店铺LOGO" prop="shop_logo">
            <div class="image-upload-container">
              <el-upload
                  class="image-uploader"
                  :action="uploadAction"
                  :show-file-list="false"
                  :before-upload="(file) => beforeImageUpload(file, 'logo')"
                  :headers="uploadHeaders"
              >
                <img v-if="shopForm.shop_logo" :src="shopForm.shop_logo" class="image-preview">
                <i v-else class="el-icon-plus image-uploader-icon"></i>
              </el-upload>
              <div class="image-upload-tips">
                <p>支持 JPG、PNG、GIF、WEBP 格式</p>
                <p>图片大小不超过 5MB</p>
                <p>建议尺寸：200x200 像素</p>
              </div>
            </div>
          </el-form-item>
        </el-row>
        <el-row :gutter="20">
          <el-form-item label="店铺横幅" prop="shop_banner">
            <div class="image-upload-container">
              <el-upload
                  class="image-uploader banner-uploader"
                  :action="uploadAction"
                  :show-file-list="false"
                  :before-upload="(file) => beforeImageUpload(file, 'banner')"
                  :headers="uploadHeaders"
              >
                <img v-if="shopForm.shop_banner" :src="shopForm.shop_banner" class="image-preview banner-preview">
                <i v-else class="el-icon-plus image-uploader-icon"></i>
              </el-upload>
              <div class="image-upload-tips">
                <p>支持 JPG、PNG、GIF、WEBP 格式</p>
                <p>图片大小不超过 5MB</p>
                <p>建议尺寸：750x300 像素</p>
              </div>
            </div>
          </el-form-item>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="省份" prop="province_code">
              <el-select
                v-model="shopForm.province_code"
                placeholder="请选择省份"
                filterable
                clearable
                @change="handleProvinceChange"
              >
                <el-option
                  v-for="item in provinceList"
                  :key="item.code"
                  :label="item.name"
                  :value="item.code"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="城市" prop="city_code">
              <el-select
                v-model="shopForm.city_code"
                placeholder="请选择城市"
                filterable
                clearable
                :disabled="!shopForm.province_code"
                @change="handleCityChange"
              >
                <el-option
                  v-for="item in cityList"
                  :key="item.code"
                  :label="item.name"
                  :value="item.code"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="区县" prop="district_code">
              <el-select
                v-model="shopForm.district_code"
                placeholder="请选择区县"
                filterable
                clearable
                :disabled="!shopForm.city_code"
                @change="handleDistrictChange"
              >
                <el-option
                  v-for="item in areaList"
                  :key="item.code"
                  :label="item.name"
                  :value="item.code"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="详细地址" prop="address">
          <el-input v-model="shopForm.address" type="textarea" placeholder="请输入店铺详细地址" />
        </el-form-item>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="营业时间" prop="business_hours">
              <el-input v-model="shopForm.business_hours" placeholder="请输入营业时间，如：09:00-22:00" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="店铺类型" prop="shop_type">
              <el-select v-model="shopForm.shop_type" placeholder="请选择店铺类型" style="width: 100%">
                <el-option label="个人" :value="1" />
                <el-option label="企业" :value="2" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="状态" prop="status">
              <el-radio-group v-model="shopForm.status">
                <el-radio :label="1">启用</el-radio>
                <el-radio :label="0">禁用</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="是否推荐" prop="is_recommend">
              <el-radio-group v-model="shopForm.is_recommend">
                <el-radio :label="1">推荐</el-radio>
                <el-radio :label="0">不推荐</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="排序" prop="sort_order">
              <el-input-number v-model="shopForm.sort_order" :min="0" style="width: 100%" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="佣金比例" prop="commission_rate">
              <el-input-number v-model="shopForm.commission_rate" :min="0" :max="1" :step="0.01" style="width: 100%" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="店铺描述" prop="shop_desc">
          <el-input v-model="shopForm.shop_desc" type="textarea" :rows="3" placeholder="请输入店铺描述" />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="handleSubmit">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 查看详情对话框 -->
    <el-dialog title="店铺详情" :visible.sync="detailVisible" width="900px">
      <div v-if="currentShop" class="shop-detail">
        <!-- 基本信息 -->
        <div class="detail-section">
          <div class="section-title">基本信息</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">店铺ID</div>
              <div class="detail-value">{{ currentShop.id }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">店铺名称</div>
              <div class="detail-value">{{ currentShop.shop_name }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">店铺LOGO</div>
              <div class="detail-value">
                <el-image 
                  v-if="currentShop.shop_logo" 
                  :src="currentShop.shop_logo" 
                  style="width: 80px; height: 80px; border-radius: 4px; cursor: pointer;"
                  fit="cover"
                  :preview-src-list="[currentShop.shop_logo]"
                />
                <span v-else>-</span>
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">店铺横幅</div>
              <div class="detail-value">
                <el-image 
                  v-if="currentShop.shop_banner" 
                  :src="currentShop.shop_banner" 
                  style="width: 300px; height: 120px; border-radius: 4px; cursor: pointer;"
                  fit="cover"
                  :preview-src-list="[currentShop.shop_banner]"
                />
                <span v-else>-</span>
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">店铺类型</div>
              <div class="detail-value">
                <el-tag :type="currentShop.shop_type == 1 ? 'primary' : 'success'">
                  {{ currentShop.shop_type == 1 ? '个人' : '企业' }}
                </el-tag>
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">状态</div>
              <div class="detail-value">
                <el-tag :type="currentShop.status == 1 ? 'success' : 'danger'">
                  {{ currentShop.status == 1 ? '启用' : '禁用' }}
                </el-tag>
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">是否推荐</div>
              <div class="detail-value">
                <el-tag :type="currentShop.is_recommend == 1 ? 'success' : 'info'">
                  {{ currentShop.is_recommend == 1 ? '推荐' : '不推荐' }}
                </el-tag>
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">店铺描述</div>
              <div class="detail-value">{{ currentShop.shop_desc || '-' }}</div>
            </div>
          </div>
        </div>

        <!-- 店主信息 -->
        <div class="detail-section">
          <div class="section-title">店主信息</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">店主ID</div>
              <div class="detail-value">{{ currentShop.owner_id || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">店主姓名</div>
              <div class="detail-value">{{ currentShop.owner_name }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">店主手机</div>
              <div class="detail-value">{{ currentShop.owner_mobile }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">联系电话</div>
              <div class="detail-value">{{ currentShop.contact_phone || '-' }}</div>
            </div>
          </div>
        </div>

        <!-- 地址信息 -->
        <div class="detail-section">
          <div class="section-title">地址信息</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">省份代码</div>
              <div class="detail-value">{{ currentShop.province_code || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">省份</div>
              <div class="detail-value">{{ currentShop.province_name || currentShop.province || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">城市代码</div>
              <div class="detail-value">{{ currentShop.city_code || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">城市</div>
              <div class="detail-value">{{ currentShop.city_name || currentShop.city || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">区县代码</div>
              <div class="detail-value">{{ currentShop.district_code || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">区县</div>
              <div class="detail-value">{{ currentShop.district_name || currentShop.district || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">详细地址</div>
              <div class="detail-value">{{ currentShop.address || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">完整地址</div>
              <div class="detail-value">
                {{ (currentShop.province_name || currentShop.province || '') +
                   (currentShop.city_name || currentShop.city || '') +
                   (currentShop.district_name || currentShop.district || '') +
                   (currentShop.address || '') || '-' }}
              </div>
            </div>
          </div>
        </div>

        <!-- 其他信息 -->
        <div class="detail-section">
          <div class="section-title">其他信息</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">营业时间</div>
              <div class="detail-value">{{ currentShop.business_hours || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">佣金比例</div>
              <div class="detail-value">{{ currentShop.commission_rate ? (currentShop.commission_rate * 100).toFixed(2) + '%' : '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">排序</div>
              <div class="detail-value">{{ currentShop.sort_order || 0 }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">创建时间</div>
              <div class="detail-value">{{ currentShop.create_time || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">更新时间</div>
              <div class="detail-value">{{ currentShop.update_time || '-' }}</div>
            </div>
          </div>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="detailVisible = false">关闭</el-button>
        <el-button type="primary" @click="handleEdit(currentShop)">编辑店铺</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getShopList, getShopDetail, createShop, updateShop, deleteShop, getProvinceList, getCityList, getAreaList } from '@/api/store'
import { uploadImage } from '@/api/upload'

export default {
  name: 'ShopManagement',
  data() {
    return {
      loading: false,
      shopList: [],
      searchForm: {
        shop_name: '',
        status: '',
        shop_type: ''
      },
      pagination: {
        page: 1,
        limit: 20,
        total: 0
      },
      dialogVisible: false,
      detailVisible: false,
      dialogTitle: '新增店铺',
      currentShop: null,
      shopForm: {
        id: null,
        shop_name: '',
        shop_desc: '',
        shop_logo: '',
        shop_banner: '',
        owner_id: null,
        owner_name: '',
        owner_mobile: '',
        contact_phone: '',
        address: '',
        province: '',
        city: '',
        district: '',
        province_code: '',
        city_code: '',
        district_code: '',
        business_hours: '',
        shop_type: 1,
        status: 1,
        is_recommend: 0,
        sort_order: 100,
        commission_rate: 0.05
      },
      shopRules: {
        shop_name: [
          { required: true, message: '请输入店铺名称', trigger: 'blur' },
          { min: 2, max: 100, message: '长度在 2 到 100 个字符', trigger: 'blur' }
        ],
        owner_id: [
          { required: true, message: '请输入店主ID', trigger: 'blur' }
        ],
        owner_name: [
          { required: true, message: '请输入店主姓名', trigger: 'blur' },
          { min: 2, max: 50, message: '长度在 2 到 50 个字符', trigger: 'blur' }
        ],
        owner_mobile: [
          { required: true, message: '请输入店主手机号', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码', trigger: 'blur' }
        ]
      },
      // 上传相关
      uploadAction: '',
      uploadHeaders: {},
      uploadLoading: null,
      // 省市区列表
      provinceList: [],
      cityList: [],
      areaList: []
    }
  },
  mounted() {
    this.fetchShopList()
    this.initUpload()
    this.fetchProvinceList()
  },
  methods: {
    // 初始化上传配置
    initUpload() {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      this.uploadHeaders = {
        'Authorization': `Bearer ${token}`
      }
      this.uploadAction = ''
    },
    
    // 上传前验证
    beforeImageUpload(file, type) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
      const isImage = allowedTypes.includes(file.type)
      if (!isImage) {
        this.$message.error('只能上传 JPG、PNG、GIF、WEBP 格式的图片！')
        return false
      }
      
      const maxSize = 5 * 1024 * 1024 // 5MB
      const isLt5M = file.size <= maxSize
      if (!isLt5M) {
        this.$message.error('图片大小不能超过 5MB！')
        return false
      }
      
      this.uploadLoading = this.$loading({
        lock: true,
        text: '上传中...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      })
      
      this.handleCustomUpload(file, type)
      return false
    },
    
    // 自定义上传方法
    async handleCustomUpload(file, type) {
      try {
        const path = type === 'logo' ? 'admin/shop/logos' : 'admin/shop/banners'
        const response = await uploadImage(file, path)
        
        if (response.code === 200) {
          if (type === 'logo') {
            this.shopForm.shop_logo = response.data.url
          } else {
            this.shopForm.shop_banner = response.data.url
          }
          this.$message.success('图片上传成功！')
        } else {
          this.$message.error(response.msg || '上传失败')
        }
      } catch (error) {
        console.error('上传失败:', error)
        this.$message.error('上传失败，请重试')
      } finally {
        if (this.uploadLoading) {
          this.uploadLoading.close()
        }
      }
    },
    
    async fetchShopList() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.page,
          limit: this.pagination.limit,
          ...this.searchForm
        }
        const response = await getShopList(params)
        this.shopList = response.data.list || []
        this.pagination.total = response.data.total || 0
      } catch (error) {
        console.error('获取店铺列表失败:', error)
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.pagination.page = 1
      this.fetchShopList()
    },
    handleReset() {
      this.searchForm = {
        shop_name: '',
        status: '',
        shop_type: ''
      }
      this.pagination.page = 1
      this.fetchShopList()
    },
    handleSizeChange(size) {
      this.pagination.limit = size
      this.fetchShopList()
    },
    handleCurrentChange(page) {
      this.pagination.page = page
      this.fetchShopList()
    },
    showCreateDialog() {
      this.dialogTitle = '新增店铺'
      this.shopForm = {
        id: null,
        shop_name: '',
        shop_desc: '',
        shop_logo: '',
        shop_banner: '',
        owner_id: null,
        owner_name: '',
        owner_mobile: '',
        contact_phone: '',
        address: '',
        province: '',
        city: '',
        district: '',
        province_code: '',
        city_code: '',
        district_code: '',
        business_hours: '',
        shop_type: 1,
        status: 1,
        is_recommend: 0,
        sort_order: 100,
        commission_rate: 0.05
      }
      this.cityList = []
      this.areaList = []
      // 重新加载省市区数据，确保数据是最新的
      this.fetchProvinceList()
      this.dialogVisible = true
    },
    async handleView(row) {
      try {
        const response = await getShopDetail(row.id)
        this.currentShop = response.data
        this.detailVisible = true
      } catch (error) {
        this.$message.error('获取店铺详情失败')
        console.error('获取店铺详情失败:', error)
      }
    },
    async handleEdit(row) {
      try {
        const response = await getShopDetail(row.id)
        this.shopForm = response.data
        // 重新加载省市区数据，确保数据是最新的
        await this.fetchProvinceList()
        // 如果有省市区代码，加载对应的城市和区县列表
        if (this.shopForm.province_code) {
          await this.fetchCityList(this.shopForm.province_code)
        }
        if (this.shopForm.city_code) {
          await this.fetchAreaList(this.shopForm.city_code)
        }
        this.dialogTitle = '编辑店铺'
        this.dialogVisible = true
        this.detailVisible = false
      } catch (error) {
        this.$message.error('获取店铺详情失败')
        console.error('获取店铺详情失败:', error)
      }
    },
    handleDelete(row) {
      this.$confirm('确认删除该店铺吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await deleteShop(row.id)
          this.$message.success('删除成功')
          this.fetchShopList()
        } catch (error) {
          console.error('删除店铺失败:', error)
        }
      })
    },
    handleSubmit() {
      this.$refs.shopForm.validate(async (valid) => {
        if (valid) {
          try {
            // 设置省市区名称
            const province = this.provinceList.find(p => p.code === this.shopForm.province_code)
            const city = this.cityList.find(c => c.code === this.shopForm.city_code)
            const area = this.areaList.find(a => a.code === this.shopForm.district_code)

            this.shopForm.province = province ? province.name : ''
            this.shopForm.city = city ? city.name : ''
            this.shopForm.district = area ? area.name : ''

            if (this.shopForm.id) {
              await updateShop(this.shopForm)
              this.$message.success('更新成功')
            } else {
              await createShop(this.shopForm)
              this.$message.success('创建成功')
            }
            this.dialogVisible = false
            this.fetchShopList()
          } catch (error) {
            console.error('操作失败:', error)
          }
        }
      })
    },
    // 获取省份列表
    async fetchProvinceList() {
      try {
        const response = await getProvinceList()
        this.provinceList = response.data || []
      } catch (error) {
        console.error('获取省份列表失败:', error)
        this.$message.error('获取省份列表失败')
      }
    },
    // 获取城市列表
    async fetchCityList(provinceCode) {
      if (!provinceCode) {
        this.cityList = []
        return
      }
      try {
        const response = await getCityList({ province_code: provinceCode })
        this.cityList = response.data || []
      } catch (error) {
        console.error('获取城市列表失败:', error)
        this.$message.error('获取城市列表失败')
      }
    },
    // 获取区县列表
    async fetchAreaList(cityCode) {
      if (!cityCode) {
        this.areaList = []
        return
      }
      try {
        const response = await getAreaList({ city_code: cityCode })
        this.areaList = response.data || []
      } catch (error) {
        console.error('获取区县列表失败:', error)
        this.$message.error('获取区县列表失败')
      }
    },
    // 省份改变事件
    handleProvinceChange(provinceCode) {
      this.shopForm.city_code = ''
      this.shopForm.district_code = ''
      this.shopForm.city = ''
      this.shopForm.district = ''
      this.cityList = []
      this.areaList = []
      if (provinceCode) {
        this.fetchCityList(provinceCode)
      }
    },
    // 城市改变事件
    handleCityChange(cityCode) {
      this.shopForm.district_code = ''
      this.shopForm.district = ''
      this.areaList = []
      if (cityCode) {
        this.fetchAreaList(cityCode)
      }
    },
    // 区县改变事件
    handleDistrictChange() {
      // 区县改变时，省市区名称在提交时会自动设置
    }
  }
}
</script>

<style scoped>
.shop-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  font-size: 24px;
  color: #303133;
}

.search-form {
  background: #f5f5f5;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  text-align: right;
}

.table-operations {
  display: flex;
  gap: 8px;
  justify-content: center;
  flex-wrap: nowrap;
}

.table-operations .el-button--text {
  padding: 2px 6px;
  font-size: 12px;
  border: none;
  background: none;
  white-space: nowrap;
  min-width: unset;
}

.table-operations .edit-btn {
  color: #409eff;
}

.table-operations .edit-btn:hover {
  color: #66b1ff;
  background-color: #ecf5ff;
}

.table-operations .delete-btn {
  color: #f56c6c;
}

.table-operations .delete-btn:hover {
  color: #f78989;
  background-color: #fef0f0;
}

.dialog-footer {
  text-align: right;
}

.shop-detail {
  padding: 10px 0;
}

.detail-section {
  margin-bottom: 30px;
}

.detail-section:last-child {
  margin-bottom: 0;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 15px;
  padding-left: 10px;
  border-left: 4px solid #409EFF;
  line-height: 20px;
}

.detail-table {
  border: 1px solid #EBEEF5;
  border-radius: 4px;
  overflow: hidden;
  background-color: #fff;
}

.detail-row {
  display: flex;
  border-bottom: 1px solid #EBEEF5;
  min-height: 50px;
  align-items: center;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  font-weight: 500;
  color: #606266;
  width: 150px;
  text-align: right;
  padding: 15px 20px;
  background-color: #FAFAFA;
  border-right: 1px solid #EBEEF5;
  flex-shrink: 0;
  box-sizing: border-box;
}

.detail-value {
  color: #303133;
  word-break: break-word;
  padding: 15px 20px;
  flex: 1;
  box-sizing: border-box;
}

/* 图片上传样式 */
.image-upload-container {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.image-uploader {
  flex-shrink: 0;
}

.image-uploader >>> .el-upload {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: all 0.3s;
  width: 148px;
  height: 148px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-uploader >>> .el-upload:hover {
  border-color: #409EFF;
}

.banner-uploader >>> .el-upload {
  width: 300px;
  height: 120px;
}

.image-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 148px;
  height: 148px;
  line-height: 148px;
  text-align: center;
}

.image-preview {
  width: 148px;
  height: 148px;
  display: block;
  object-fit: cover;
}

.banner-preview {
  width: 300px;
  height: 120px;
}

.image-upload-tips {
  flex: 1;
  color: #909399;
  font-size: 12px;
  line-height: 1.8;
}

.image-upload-tips p {
  margin: 0;
  padding: 2px 0;
}
</style>